import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { avatarColor, initials } from "@/lib/utils2";

// Icône lucide par métier (voir PROFESSION_CATEGORIES côté backend).
const PROFESSION_ICONS = {
  plomberie: "Droplets", electricite: "Zap", carrelage: "Grid3x3", peinture: "Paintbrush",
  menuiserie: "Hammer", climatisation: "Wind", serrurerie: "KeyRound", macon: "Building2",
  jardinage: "Trees", nettoyage: "Sparkles", electromenager: "Plug", autre: "Wrench",
};
const PROFESSION_COLORS = {
  plomberie: "#3254C6", electricite: "#F59E0B", carrelage: "#0A4D3C", peinture: "#C05A46",
  menuiserie: "#8B5CF6", climatisation: "#06B6D4", serrurerie: "#64748B", macon: "#B45309",
  jardinage: "#10B981", nettoyage: "#0891B2", electromenager: "#6366F1", autre: "#94A3B8",
};

const empty = { name: "", profession: "plomberie", phone: "", zone: "", note: "" };

export default function Professionals() {
  const { t } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pros, setPros] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() =>
    api.get("/professionals").then((r) => setPros(r.data)).catch(() => toast.error(t("load_failed"))), [t]);
  useEffect(() => {
    load();
    api.get("/professions").then((r) => setProfessions(r.data)).catch(() => {});
  }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error(t("pro_name"));
    setBusy(true);
    try {
      await api.post("/professionals", form);
      toast.success(t("saved"));
      setForm(empty); setOpen(false); load();
    } catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const rate = async (id, stars) => {
    await api.post(`/professionals/${id}/rate`, { stars });
    load();
  };

  const remove = async (id) => {
    try { await api.delete(`/professionals/${id}`); toast.success(t("delete")); load(); }
    catch { toast.error("Erreur"); }
  };

  const shown = filter === "all" ? pros : pros.filter((p) => p.profession === filter);
  const activeCats = [...new Set(pros.map((p) => p.profession))];

  return (
    <div className="space-y-4" data-testid="professionals-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/more")} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0" data-testid="pros-back">
          <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-tight">{t("pro_directory")}</h1>
          <p className="text-sm text-muted-foreground">{t("pro_directory_sub")}</p>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="add-pro-btn"
          className="h-10 px-3 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1 shrink-0">
          <Icons.Plus className="h-4 w-4" /> {t("add_pro")}
        </button>
      </div>

      {/* Formulaire d'ajout */}
      {open && (
        <div className="rounded-2xl bg-card border border-border p-4 space-y-3" data-testid="pro-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("pro_name")}
            data-testid="pro-name" className="w-full h-12 rounded-xl border border-input bg-background px-3 outline-none" />
          <div>
            <label className="text-sm font-semibold text-muted-foreground">{t("pro_profession")}</label>
            <select value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} data-testid="pro-profession"
              className="w-full h-12 rounded-xl border border-input bg-background px-3 mt-1 outline-none">
              {professions.map((c) => <option key={c} value={c}>{t("prof_" + c)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("pro_phone")}
              data-testid="pro-phone" className="w-full h-12 rounded-xl border border-input bg-background px-3 outline-none" />
            <input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder={t("pro_zone")}
              data-testid="pro-zone" className="w-full h-12 rounded-xl border border-input bg-background px-3 outline-none" />
          </div>
          <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("pro_note")} rows={2}
            data-testid="pro-note" className="w-full rounded-xl border border-input bg-background px-3 py-2 outline-none" />
          <button onClick={create} disabled={busy} data-testid="pro-submit"
            className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60">{t("create")}</button>
        </div>
      )}

      {/* Filtres par métier */}
      {activeCats.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={t("all_cat")} testid="pro-filter-all" />
          {activeCats.map((c) => (
            <FilterChip key={c} active={filter === c} onClick={() => setFilter(c)} label={t("prof_" + c)} testid={`pro-filter-${c}`} />
          ))}
        </div>
      )}

      {/* Liste */}
      {shown.length === 0 && (
        <div className="py-16 flex flex-col items-center text-center gap-3">
          <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center">
            <Icons.Contact className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground font-medium max-w-xs">{t("no_pros")}</p>
        </div>
      )}

      <div className="space-y-3">
        {shown.map((p) => {
          const Ico = Icons[PROFESSION_ICONS[p.profession]] || Icons.Wrench;
          const color = PROFESSION_COLORS[p.profession] || "#94A3B8";
          const waNumber = (p.phone || "").replace(/[^0-9]/g, "");
          const canDelete = p.recommended_by_id === user?.id || user?.role === "president";
          return (
            <div key={p.id} className={`rounded-2xl bg-card border p-4 ${p.top_rated ? "border-warning/60 ring-1 ring-warning/30" : "border-border"}`} data-testid={`pro-${p.id}`}>
              {p.top_rated && (
                <div className="flex items-center gap-1.5 mb-3 text-warning font-bold text-xs uppercase tracking-wide" data-testid={`pro-badge-${p.id}`}>
                  <Icons.Award className="h-4 w-4" />
                  {t("neighbourhood_pick")}
                </div>
              )}
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: color + "22" }}>
                  <Ico className="h-6 w-6" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold truncate">{p.name}</p>
                      <p className="text-xs font-semibold" style={{ color }}>{t("prof_" + p.profession)}{p.zone ? ` · ${p.zone}` : ""}</p>
                    </div>
                    {canDelete && (
                      <button onClick={() => remove(p.id)} data-testid={`pro-delete-${p.id}`} className="text-muted-foreground p-1">
                        <Icons.Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Note moyenne */}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Stars value={Math.round(p.avg_rating)} />
                    <span className="text-xs text-muted-foreground">{p.avg_rating > 0 ? `${p.avg_rating} · ${p.rating_count} ${t("reviews")}` : "—"}</span>
                  </div>

                  {p.note && <p className="text-sm text-muted-foreground mt-2">“{p.note}”</p>}

                  <div className="flex items-center gap-2 mt-2">
                    <div className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: avatarColor(p.recommended_by) }}>
                      {initials(p.recommended_by)}
                    </div>
                    <span className="text-xs text-muted-foreground">{t("pro_recommended_by")} {p.recommended_by}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3">
                    {p.phone && (
                      <>
                        <a href={`tel:${p.phone}`} data-testid={`pro-call-${p.id}`}
                          className="flex-1 h-10 rounded-xl bg-brand text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                          <Icons.Phone className="h-4 w-4" /> {t("pro_call")}
                        </a>
                        <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" data-testid={`pro-wa-${p.id}`}
                          className="flex-1 h-10 rounded-xl bg-[#25D366] text-white text-sm font-semibold flex items-center justify-center gap-1.5">
                          <Icons.MessageCircle className="h-4 w-4" /> {t("pro_whatsapp")}
                        </a>
                      </>
                    )}
                  </div>

                  {/* Noter */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                    <span className="text-xs font-semibold text-muted-foreground">{t("pro_rate")}:</span>
                    <RateStars onRate={(s) => rate(p.id, s)} testid={`pro-rate-${p.id}`} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-9 px-4 rounded-full font-semibold text-sm whitespace-nowrap transition-colors ${active ? "bg-brand text-white" : "bg-secondary text-secondary-foreground"}`}>
      {label}
    </button>
  );
}

function Stars({ value }) {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Icons.Star key={i} className="h-4 w-4" fill={i <= value ? "#F59E0B" : "none"} stroke={i <= value ? "#F59E0B" : "#cbd5e1"} />
      ))}
    </div>
  );
}

function RateStars({ onRate, testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => onRate(i)}
          data-testid={`${testid}-${i}`} className="p-0.5 active:scale-90 transition-transform">
          <Icons.Star className="h-6 w-6" fill={i <= hover ? "#F59E0B" : "none"} stroke={i <= hover ? "#F59E0B" : "#94a3b8"} />
        </button>
      ))}
    </div>
  );
}
